import type { AgentTurnBody, AgentTurnDone, AgentTurnError } from '@/hooks/useAgentTurnStream';

export interface TurnRequest extends AgentTurnBody {
  sessionId: number;
  action: 'send' | 'regenerate' | 'edit';
}

export interface TurnStep {
  id: string;
  tool: string;
  label: string;
  status: 'running' | 'ok' | 'failed';
  durationMs?: number;
}

/**
 * The live (unsaved) part of one turn. `streaming` while events arrive, `finishing` while the
 * saved thread is refetched, `failed` until the advisor retries or dismisses the error.
 */
export interface LiveTurn {
  request: TurnRequest;
  phase: 'streaming' | 'finishing' | 'failed';
  content: string;
  steps: TurnStep[];
  userMessageId?: number;
  turnId?: string;
  done?: AgentTurnDone;
  error?: AgentTurnError;
}

export type TurnAction =
  | { type: 'start'; request: TurnRequest }
  | { type: 'meta'; userMessageId: number; turnId?: string }
  | { type: 'status'; tool: string; label: string; toolUseId: string }
  | { type: 'tool'; tool: string; label: string; status: 'ok' | 'failed'; durationMs: number }
  | { type: 'token'; delta: string }
  | { type: 'replace'; content: string }
  | { type: 'done'; data: AgentTurnDone }
  | { type: 'error'; data: AgentTurnError }
  /** The saved thread has been refetched, so the live bubble can go. */
  | { type: 'settled' }
  | { type: 'dismiss' };

export function turnReducer(state: LiveTurn | null, action: TurnAction): LiveTurn | null {
  if (action.type === 'start') return { request: action.request, phase: 'streaming', content: '', steps: [] };
  if (action.type === 'dismiss') return null;
  if (!state) return state;
  switch (action.type) {
    case 'meta':
      return { ...state, userMessageId: action.userMessageId, turnId: action.turnId };
    case 'status':
      if (state.steps.some((s) => s.id === action.toolUseId)) return state;
      return {
        ...state,
        steps: [...state.steps, { id: action.toolUseId, tool: action.tool, label: action.label, status: 'running' }],
      };
    case 'tool': {
      const i = state.steps.findIndex((s) => s.tool === action.tool && s.status === 'running');
      const step = { tool: action.tool, label: action.label, status: action.status, durationMs: action.durationMs };
      const steps =
        i >= 0
          ? state.steps.map((s, j) => (j === i ? { ...s, ...step } : s))
          : [...state.steps, { id: `${action.tool}-${state.steps.length}`, ...step }];
      return { ...state, steps };
    }
    case 'token':
      return { ...state, content: state.content + action.delta };
    case 'replace':
      return { ...state, content: action.content };
    case 'done': {
      const steps = state.steps.map((s) => (s.status === 'running' ? { ...s, status: 'ok' as const } : s));
      return {
        ...state,
        steps,
        done: action.data,
        content: action.data.content || state.content,
        phase: state.error ? 'failed' : 'finishing',
      };
    }
    case 'error':
      return { ...state, error: action.data, phase: 'failed' };
    case 'settled':
      // A failed turn keeps its error card (with Retry); any partial answer is now in the thread.
      return state.phase === 'failed' ? { ...state, content: '', steps: [] } : null;
    default:
      return state;
  }
}

/**
 * What Retry sends. Once the server has saved the advisor's message (meta received), retrying
 * regenerates from it, so the message is not duplicated; otherwise the same request is repeated.
 */
export function retryRequest(turn: LiveTurn): TurnRequest {
  if (turn.userMessageId) return { sessionId: turn.request.sessionId, action: 'regenerate', content: '' };
  return turn.request;
}

/**
 * Hide messages a live regenerate/edit is replacing, until the refetched thread reflects it.
 * Messages already marked `supersededAt` by the server are always hidden.
 */
export function visibleMessages<T extends { id: number | string; role: string; metadata?: any }>(
  messages: T[],
  turn: LiveTurn | null,
): T[] {
  const active = messages.filter((m) => !m.metadata?.supersededAt);
  if (!turn || turn.phase === 'failed') return active;
  const { action, targetMessageId } = turn.request;
  if (action === 'edit') {
    const i = active.findIndex((m) => m.id === targetMessageId);
    // Keep the optimistic copy of the edited message, which is appended at the end.
    return i >= 0 ? active.filter((m, j) => j < i || (j === active.length - 1 && Number(m.id) < 0)) : active;
  }
  if (action === 'regenerate') {
    const lastUser = active.map((m) => m.role).lastIndexOf('user');
    return lastUser >= 0 ? active.slice(0, lastUser + 1) : active;
  }
  return active;
}
